// Refactored to remove _boundingBox, imageToPatient, _patientToImage. Grid now authoritative.
import {
    vec2,
    vec3,
    mat4,
    quat,
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec3,
} from 'gl-matrix';
import { VertexArrayObject } from '~/engine/core/utils/VertexArrayObject';
import { SignedDistanceField } from '../../core/models/SignedDistanceField';
import { RenderStorage } from '../../core/render/RenderStorage';
import { SdfGenerator } from '../../util/algorithms/sdf/SdfGenerator';
import { RotatedBoundingBox } from '../common/RotatedBoundingBox';
import { ContourSequence } from './ContourSequence';
import { Grid } from '../common/Grid';
import { calculateVolumeMm3 } from '~/engine/util/algorithms/sdf/SDFVolumeCalculation';
import {
    RoiStatistics,
    calculateContourStatistics,
} from '~/engine/util/algorithms/sdf/ContourStatistics';

/**
 * {@link ContourTexture} represents the contour data using a {@link SignedDistanceField} (SDF) texture.\
 * This presentation is used by most contouring tools and visualizations.
 */
export class ContourTexture {
    /**
     * The default {@link margin} that is added around each side of the contour data when generating texture.\
     * The unit is millimeters.
     */
    public static DEFAULT_MARGIN = 10;
    public static SDF_THRESHOLD = 127.5;

    /**
     * Conversion factor between physical millimetres and this texture's
     * native SDF byte (Uint8) distance units.
     *
     * The SDF stores a signed distance clamped to ±{@link margin} and
     * mapped onto `[0, 255]` with the iso at {@link SDF_THRESHOLD} (see
     * `quantizeSdfMmToUint8`, whose `scale = threshold / falloffMm`).
     * One byte therefore equals `margin / SDF_THRESHOLD` mm — the same
     * on every axis and independent of voxel resolution.
     *
     * The iso threshold is a codebase-wide constant; the falloff is
     * this texture's own per-instance {@link margin}, so the factor
     * reflects the SDF's actual encoding rather than a fixed default.
     */
    public sdfBytesPerMm(): number {
        return ContourTexture.SDF_THRESHOLD / this._margin;
    }

    /**
     * The amount of padding added to each side of the contour data in millimeters.\
     * This is useful to support better visualization.\
     * Padding provides smoother contours and improves the quality of contouring tools
     * and operations such as interpolation.
     */
    private _margin: number;

    /**
     * The Signed Distance Field that contains the WebGL rendering texture data.
     */
    private _sdf: SignedDistanceField;

    // grid geometry of the Signed Distance Field with transformation from patient coordinates to voxel space
    private _textureGrid: Grid;

    private readonly _segmentResolution: number;
    private _imageGrid: Grid;

    /**
     * New constructor:
     *  - imageGrid: master grid defining orientation & voxel spacing
     *  - sizePx: voxel dimensions (nx, ny, nz) of the SDF texture
     *  - translationWorld: world-space (patient-space) position of voxel (0,0,0)
     *    i.e. the corner of the first (corner) voxel origin in patient coordinates
     *  - options.marginMm: optional padding metadata (does not alter transform)
     */
    /**
     * @param imageGrid Master grid defining orientation & voxel spacing.
     * @param sizePx Voxel dimensions (nx, ny, nz) of the SDF texture.
     * @param translationWorld World-space position of voxel (0,0,0).
     * @param marginMm Padding metadata (does not alter transform).
     * @param resolution Optional voxel spacing override. When omitted,
     *        imageGrid's spacing is used. Cursor objects with custom
     *        resolution (e.g. AdaptiveSphere) pass their own spacing.
     */
    public constructor(
        imageGrid: Grid,
        sizePx: ReadonlyVec3,
        translationWorld: ReadonlyVec3,
        marginMm: number = ContourTexture.DEFAULT_MARGIN,
        resolution?: ReadonlyVec3
    ) {
        this._imageGrid = imageGrid;
        this._margin = marginMm;

        if (imageGrid.resX !== imageGrid.resY) {
            console.warn(
                `ContourTexture: imageGrid planar resolutions differ (resX=${imageGrid.resX}, resY=${imageGrid.resY})`
            );
        }
        this._segmentResolution = imageGrid.resX;

        this._textureGrid = this.buildTextureGrid(
            sizePx,
            translationWorld,
            resolution
        );

        this._sdf = new SignedDistanceField(sizePx);
    }

    /**
     * Builds a texture grid from the image grid orientation and
     * the provided size, position, and optional resolution.
     */
    private buildTextureGrid(
        sizePx: ReadonlyVec3,
        translationWorld: ReadonlyVec3,
        resolution?: ReadonlyVec3
    ): Grid {
        const orientationMat3 = this._imageGrid.getOrientation();
        const orientationQuat = quat.fromMat3(quat.create(), orientationMat3);
        const transform = mat4.fromRotationTranslation(
            mat4.create(),
            orientationQuat,
            vec3.fromValues(
                translationWorld[0],
                translationWorld[1],
                translationWorld[2]
            )
        );

        return new Grid(
            sizePx[0],
            sizePx[1],
            sizePx[2],
            resolution ? resolution[0] : this._imageGrid.resX,
            resolution ? resolution[1] : this._imageGrid.resY,
            resolution ? resolution[2] : this._imageGrid.resZ,
            transform
        );
    }

    /**
     * Updates the spatial extent and position of this
     * {@link ContourTexture} without reallocating the SDF when
     * the size has not changed. Used by cursor objects that
     * reposition frequently (e.g. on every mouse move).
     *
     * @param sizePx New voxel dimensions.
     * @param translationWorld New world-space corner position.
     */
    public updateGrid(
        sizePx: ReadonlyVec3,
        translationWorld: ReadonlyVec3
    ): void {
        const oldSize = this._textureGrid.getSize();
        this._textureGrid = this.buildTextureGrid(
            sizePx,
            translationWorld,
            vec3.fromValues(
                this._textureGrid.resX,
                this._textureGrid.resY,
                this._textureGrid.resZ
            )
        );
        if (
            oldSize[0] !== sizePx[0] ||
            oldSize[1] !== sizePx[1] ||
            oldSize[2] !== sizePx[2]
        ) {
            this._sdf.resize(sizePx);
        }
    }

    public static createNewWithMargin(
        expansionMm: number,
        source: ContourTexture
    ): ContourTexture {
        if (expansionMm <= 0) {
            return source.clone();
        }
        const toPx = (res: number) => Math.ceil(expansionMm / res);

        const inputGrid = source.textureGrid.expandOrShrink(
            toPx(source.textureGrid.resX),
            toPx(source.textureGrid.resY),
            toPx(source.textureGrid.resZ),
            true
        );
        return new ContourTexture(
            source.grid,
            inputGrid.getSize(),
            inputGrid.getPosition(),
            source._margin
        );
    }

    /**
     * Create a deep copy of this ContourTexture.
     * The cloned texture will have:
     * - Same grid configuration (orientation, spacing, dimensions)
     * - Copy of SDF pixel data (independent GPU texture)
     * - Same margin and resolution settings
     * - Fresh cached data (volumes, pixels will be recalculated on demand)
     *
     * @param options Optional overrides for the clone
     * @returns New ContourTexture instance with copied data
     */
    public clone(): ContourTexture {
        // Clone the texture grid to ensure independence
        const clonedGrid = this._textureGrid.clone();

        // Create new ContourTexture with same configuration
        const cloned = new ContourTexture(
            this._imageGrid, // Reference to same image grid is fine (immutable)
            this._sdf.size,
            this._textureGrid.getPosition(),
            this._margin
        );

        // Replace the auto-generated grid with our cloned grid
        cloned._textureGrid = clonedGrid;

        // Copy SDF pixel data if source has data
        if (this.isEmpty === false) {
            const pixelArrays = this.pixels; // This will cache if needed
            cloned.resetSdfAndGrid(pixelArrays, clonedGrid);
        }

        return cloned;
    }

    /**
     * Create a deep copy of this ContourTexture with new SDF pixel data
     * (and optionally a new grid). Combines {@link clone} + {@link resetSdfAndGrid}
     * into a single convenience call.
     *
     * @param pixels New per-slice SDF pixel data
     * @param grid Optional new grid; defaults to the source textureGrid
     * @returns New independent ContourTexture with the provided pixel data
     */
    public cloneWithNewSdf(pixels: Uint8Array[], grid?: Grid): ContourTexture {
        const result = this.clone();
        result.resetSdfAndGrid(pixels, grid ?? this.textureGrid);
        return result;
    }

    /**
     * Factory method to create a minimal empty ContourTexture with 1x1x1 voxel size.
     * Useful for placeholder structures or initialization when actual contour data is unavailable.
     *
     * @param imageGrid Master grid defining orientation & voxel spacing alignment
     * @returns New minimal ContourTexture with single voxel at image grid origin
     */
    public static createEmpty(imageGrid: Grid): ContourTexture {
        return new ContourTexture(
            imageGrid,
            vec3.fromValues(1, 1, 1), // Minimal 1x1x1 voxel size
            imageGrid.getPosition() // Aligned to image grid origin
        );
    }

    // Geometry / orientation
    /**
     * {@link ReadonlyMat3 rotation} from ImageSpace to PatientSpace.\
     * This is the {@link RotatedBoundingBox.rotation} of the {@link _boundingBox}.
     */
    public get rotation(): ReadonlyMat3 {
        return this._textureGrid.getOrientation();
    }

    /**
     * The common Grid where the texture is contained in.\
     * Equals the image Grid.\
     */
    public get grid(): Grid {
        return this._imageGrid;
    }

    public get textureGrid(): Grid {
        return this._textureGrid;
    }

    /**
     * Texture-UV Z range `[zMin, zMax]` corresponding to the
     * image grid's first and last slice center positions. May
     * fall outside `[0, 1]` when the contour texture's Z extent
     * sits strictly inside the image.
     */
    public getImageZUvRange(): vec2 {
        const imageGrid = this._imageGrid;
        if (imageGrid.sizeZ <= 0) {
            return vec2.fromValues(0, 1);
        }
        const firstCenterWorld = imageGrid.voxelCenterToWorldSpace(
            vec3.fromValues(0, 0, 0)
        );
        const lastCenterWorld = imageGrid.voxelCenterToWorldSpace(
            vec3.fromValues(0, 0, imageGrid.sizeZ - 1)
        );
        const firstUv =
            this._textureGrid.worldToTextureUVPosition(firstCenterWorld);
        const lastUv =
            this._textureGrid.worldToTextureUVPosition(lastCenterWorld);
        const zMin = Math.min(firstUv[2], lastUv[2]);
        const zMax = Math.max(firstUv[2], lastUv[2]);
        return vec2.fromValues(zMin, zMax);
    }

    private _isEmptyCached: boolean | undefined;

    public get isEmpty(): boolean {
        if (
            !this._sdf ||
            this._sdf.size[0] <= 1 ||
            this._sdf.size[1] <= 1 ||
            this._sdf.size[2] <= 1
        ) {
            return true;
        }
        if (this._isEmptyCached === undefined) {
            this._isEmptyCached = !this._sdf.hasPixelsAbove(
                ContourTexture.SDF_THRESHOLD
            );
        }
        return this._isEmptyCached;
    }

    private _volumeCachedCm3: number | undefined;
    private _volumeCacheVersion = -1;
    private _volumeCacheGrid: Grid | undefined;

    /**
     * Calculate structure volume using trilinear interpolation at SDF boundaries.
     * More accurate than simple binary thresholding by accounting for partial voxel occupancy.
     */
    public get volumeCm3(): number {
        if (
            this._volumeCachedCm3 === undefined ||
            this._volumeCacheVersion !== this._sdf.dataVersion ||
            this._volumeCacheGrid !== this._textureGrid
        ) {
            const volumeMm3 = calculateVolumeMm3(this._textureGrid, this._sdf);
            this._volumeCachedCm3 = volumeMm3 / 1000;
            this._volumeCacheVersion = this._sdf.dataVersion;
            this._volumeCacheGrid = this._textureGrid;
        }
        return this._volumeCachedCm3;
    }

    private _statsCached: RoiStatistics | undefined;
    private _statsCacheVersion = -1;
    private _statsCacheGrid: Grid | undefined;

    /**
     * SDF-derived shape statistics (surface area, sphericity, center of mass,
     * bounding box). Cached like {@link volumeCm3} and invalidated on SDF
     * data-version or grid change. Volume keeps its own getter and feeds
     * sphericity here.
     */
    public get statistics(): RoiStatistics {
        if (
            this._statsCached === undefined ||
            this._statsCacheVersion !== this._sdf.dataVersion ||
            this._statsCacheGrid !== this._textureGrid
        ) {
            const aabb = this.contentBoundingBox.toAxisAlignedBoundingBox();
            this._statsCached = calculateContourStatistics(
                this._textureGrid,
                this._sdf,
                this.volumeCm3 * 1000,
                aabb.bounds
                    ? { min: aabb.bounds.min, max: aabb.bounds.max }
                    : null
            );
            this._statsCacheVersion = this._sdf.dataVersion;
            this._statsCacheGrid = this._textureGrid;
        }
        return this._statsCached;
    }

    /**
     * Method to get the PatientSpace {@link ReadonlyVec3 coordinates} of this {@link ContourTexture}'s center point.\
     * This is the {@link RotatedBoundingBox.center centerPoint} of the {@link _boundingBox} rotated by the {@link rotation} to convert from ImageSpace to PatientSpace.
     */
    public get center(): ReadonlyVec3 {
        return this._textureGrid.center;
    }

    public get widthMm(): number {
        return this._textureGrid.sizeXMM;
    }

    public get heightMm(): number {
        return this._textureGrid.sizeYMM;
    }

    public get sizePx(): ReadonlyVec3 {
        return this._sdf ? this._sdf.size : vec3.create();
    }

    /**
     * Method to get the planar resolution of the texture SDF.\
     * The resolution should be the same as the image resolution.\
     * The unit is mm/px.
     */
    public get xyResolution(): number {
        return this._segmentResolution;
    }

    /**
     * Method to get the margin around the structure outline for distance transform.\
     */
    public get margin(): number {
        return this._margin;
    }

    public get signedDistanceField(): SignedDistanceField {
        return this._sdf;
    }

    /*
     * Patient to texture UV ([0, 1] space) transformation matrix.
     */
    public get patientToImage(): ReadonlyMat4 {
        return this._textureGrid.worldToTextureUV;
    }

    public get fullBoundingBox(): RotatedBoundingBox {
        // Construct rotated box from grid
        const bb = new RotatedBoundingBox();
        const orient = this._textureGrid.getOrientation();
        bb.rotate(orient);
        const pos = this._textureGrid.getPosition();
        const max = this._textureGrid.toWorldSpace(this._textureGrid.getSize());
        bb.bounds = {
            min: this._textureGrid.rotateToGrid(pos),
            max: this._textureGrid.rotateToGrid(max),
        };
        return bb;
    }

    /**
     * Calculates the bounding box from actual SDF pixel content.
     * This scans through the SDF pixels to find the min/max coordinates
     * where pixel values are above the SDF threshold (indicating content inside the structure).
     *
     * @returns A {@link RotatedBoundingBox} that tightly bounds the actual SDF content,
     *          or an empty RotatedBoundingBox if the texture is empty or has no content.
     */
    public get contentBoundingBox(): RotatedBoundingBox {
        if (this.isEmpty) {
            return new RotatedBoundingBox();
        }

        const bounds = this._sdf.getContentBounds(ContourTexture.SDF_THRESHOLD);
        if (!bounds) {
            return new RotatedBoundingBox();
        }

        // Add 1 to max to include the full extent of the last voxel
        vec3.add(bounds.max, bounds.max, vec3.fromValues(1, 1, 1));

        // Convert pixel coordinates to world space
        const minWorld = this._textureGrid.toWorldSpace(bounds.min);
        const maxWorld = this._textureGrid.toWorldSpace(bounds.max);

        // Construct the bounding box with proper rotation
        const bb = new RotatedBoundingBox();
        const orient = this._textureGrid.getOrientation();
        bb.rotate(orient);
        bb.bounds = {
            min: this._textureGrid.rotateToGrid(minWorld),
            max: this._textureGrid.rotateToGrid(maxWorld),
        };

        return bb;
    }

    public toImageSpace(patient: ReadonlyVec3): vec3 {
        return vec3.transformMat4(vec3.create(), patient, this.patientToImage);
    }

    public toPatientSpace(image: ReadonlyVec3): vec3 {
        return vec3.transformMat4(
            vec3.create(),
            image,
            this._textureGrid.voxelToWorld
        );
    }

    public sliceIndexToNormalPosition(index: number): number {
        if (index < 0 || index >= this._textureGrid.sizeZ) {
            throw new Error(
                `slice index ${index} out of range (${this._textureGrid.sizeZ})`
            );
        }
        return this._textureGrid.getSliceCenterNormalPosition(index);
    }

    public generateVertex(x: 1 | -1, y: 1 | -1, normalPosition: number): vec3 {
        // Build from grid basis
        const origin = this._textureGrid.getPosition();
        const vx = vec3.scale(
            vec3.create(),
            this._textureGrid.getUnitX(),
            this._textureGrid.sizeXMM
        );
        const vy = vec3.scale(
            vec3.create(),
            this._textureGrid.getUnitY(),
            this._textureGrid.sizeYMM
        );
        const p = vec3.clone(origin);
        if (x === 1) {
            vec3.add(p, p, vx);
        }
        if (y === 1) {
            vec3.add(p, p, vy);
        }
        // normalPosition already patient-space coordinate along Z; project along uz from origin.z plane
        const uz = this._textureGrid.getUnitZ();
        const z0 = vec3.dot(origin, uz);
        const dz = normalPosition - z0;
        vec3.add(p, p, vec3.scale(vec3.create(), uz, dz));
        return p;
    }

    /**
     * Method to calculate the intersection between this texture and a projection plane.\
     */
    public crossSection(
        toProjection: ReadonlyMat4
    ): VertexArrayObject | undefined {
        return this._textureGrid.crossSection(toProjection);
    }

    public invalidateCachedPixels() {
        this._sdf.invalidateCachedPixels();
        this._isEmptyCached = undefined;
    }

    public get pixels(): Uint8Array[] {
        return this._sdf.pixels;
    }

    /**
     * Method to {@link SignedDistanceField.bind bind} the {@link sdf} {@link WebGLTexture texture} of this {@link ContourTexture}.
     * @returns True if the {@link sdf} was successfully bound, false otherwise.
     */
    public bindTexture(): boolean {
        return this._sdf ? this._sdf.bind() : false;
    }

    /**
     * Method to unbind the {@link SignedDistanceField} texture of this {@link ContourTexture}.\
     * This will unbind any texture that has been bound.
     */
    public unbindTexture(): void {
        this._sdf?.unbind();
    }

    /**
     * Factory method to create a new ContourTexture from a ContourSequence.
     *
     * Calculates tight bounds from contour data, creates appropriately-sized texture,
     * and renders the SDF using cached shaders for optimal performance.
     *
     * @param imageGrid Master grid defining orientation & voxel spacing alignment
     * @param contourSequence Input contour data to convert to SDF
     * @returns New ContourTexture with rendered SDF, or empty texture if no contours were present
     */
    public static fromContourSequence(
        imageGrid: Grid,
        contourSequence: ContourSequence
    ): { texture: ContourTexture; renderErrors?: string } {
        if (contourSequence.contourSlices.length === 0) {
            return {
                texture: ContourTexture.createEmpty(imageGrid),
                renderErrors: undefined,
            };
        }

        const marginMm = ContourTexture.DEFAULT_MARGIN;
        const res = imageGrid.getRes();

        const originWorld = imageGrid.getPosition();
        const R = imageGrid.getOrientation();
        const Rinv = imageGrid.getOrientationInverse();

        const worldToImageMm = (pWorld: ReadonlyVec3): vec3 => {
            const d = vec3.sub(vec3.create(), pWorld, originWorld);
            return vec3.transformMat3(d, d, Rinv);
        };

        const imageMmToWorld = (pImage: ReadonlyVec3): vec3 => {
            const d = vec3.transformMat3(vec3.create(), pImage, R);
            return vec3.add(d, originWorld, d);
        };

        // 1) Compute AABB in image aligned mm coordinates
        const imageMin = vec3.fromValues(+Infinity, +Infinity, +Infinity);
        const imageMax = vec3.fromValues(-Infinity, -Infinity, -Infinity);

        for (const slice of contourSequence.contourSlices) {
            for (const contour of slice.contours) {
                for (const vWorld of contour) {
                    const vImage = worldToImageMm(vWorld);
                    vec3.min(imageMin, imageMin, vImage);
                    vec3.max(imageMax, imageMax, vImage);
                }
            }
        }

        vec3.sub(
            imageMin,
            imageMin,
            vec3.fromValues(marginMm, marginMm, marginMm)
        );
        vec3.add(
            imageMax,
            imageMax,
            vec3.fromValues(marginMm, marginMm, marginMm)
        );

        const snappedImageMin = imageGrid.floorMmToVoxelBoundary(imageMin);
        const snappedImageMax = imageGrid.ceilMmToVoxelBoundary(imageMax);

        const imageExtent = vec3.sub(
            vec3.create(),
            snappedImageMax,
            snappedImageMin
        );
        const sizePx = vec3.fromValues(
            Math.max(1, Math.ceil(imageExtent[0] / res[0])),
            Math.max(1, Math.ceil(imageExtent[1] / res[1])),
            Math.max(1, Math.ceil(imageExtent[2] / res[2]))
        );

        const minWorld = imageMmToWorld(snappedImageMin);

        const texture = new ContourTexture(imageGrid, sizePx, minWorld);

        // Render contours to SDF using cached shaders
        let renderErrors: string | undefined;
        try {
            const sdfGenerator = RenderStorage.getRenderer(SdfGenerator);
            renderErrors = sdfGenerator.renderSdf(
                texture,
                contourSequence,
                undefined,
                marginMm,
                marginMm
            );
        } catch (error) {
            texture.delete();
            throw new Error(
                `Failed to render SDF: ${
                    error instanceof Error ? error.message : String(error)
                }`
            );
        }

        return { texture, renderErrors };
    }

    /**
     * Replace the SDF pixel data without changing the grid or
     * reallocating the GPU texture. The provided arrays must
     * match the current SDF dimensions.
     */
    public resetSdfData(arrays: Uint8Array[]): void {
        this._sdf.arrayToTexture(arrays);
        this._isEmptyCached = undefined;
    }

    public resetGrid(newGrid: Grid): void {
        this._textureGrid = newGrid;
    }

    /**
     * Replace both the SDF (from pixel arrays) and texture grid in one operation.
     * This is useful when you have a new Grid definition and corresponding pixel data.
     *
     * @param arrays Array of Uint8Array slices for the new SDF
     * @param newGrid New Grid instance that defines the texture coordinate system
     */
    public resetSdfAndGrid(arrays: Uint8Array[], newGrid: Grid): void {
        const gridSize = newGrid.getSize();
        const expectedSlices = gridSize[2];
        const expectedPixelsPerSlice = gridSize[0] * gridSize[1];

        if (arrays.length !== expectedSlices) {
            throw new Error(
                `Array slice count mismatch: grid expects ${expectedSlices} slices, got ${arrays.length}`
            );
        }

        for (let z = 0; z < arrays.length; z++) {
            if (arrays[z].length !== expectedPixelsPerSlice) {
                throw new Error(
                    `Slice ${z} size mismatch: grid expects ${expectedPixelsPerSlice} pixels (${gridSize[0]}×${gridSize[1]}), got ${arrays[z].length}`
                );
            }
        }

        // Clean up old SDF resources
        if (this._sdf) {
            this._sdf.delete();
        }

        // Create new SDF with grid dimensions
        this._sdf = new SignedDistanceField(gridSize);

        // Replace texture grid and upload pixel data
        this._textureGrid = newGrid;
        this.resetSdfData(arrays);
    }

    /**
     * Method to delete this {@link ContourTexture}.\
     * This de-allocates all the WebGL memory reserved by this {@link ContourTexture}.\
     * Currently that only means deleting the {@link SignedDistanceField}.
     */
    public delete() {
        this._sdf?.delete();
    }

    /**
     * Create a new ContourTexture whose Grid covers both input textures.
     * Assumptions:
     *  - Grids have identical orientation (same rotation)
     *  - Grids share identical voxel resolutions (resX,resY,resZ)
     *  - No resampling; result uses the same resolution
     * Optional marginMm adds padding (in mm) on all sides.
     * Result SDF is empty; caller must populate.
     */
    public static createUnionAligned(
        a: ContourTexture,
        b: ContourTexture,
        marginMm = 0
    ): ContourTexture {
        const unionGrid = Grid.unionAligned(
            a.textureGrid,
            b.textureGrid,
            marginMm
        );

        // Clamp to image bounds (safety net for imported/legacy data)
        const clampedGrid = ContourTexture.clampGridToImage(unionGrid, a.grid);

        return new ContourTexture(
            a.grid,
            clampedGrid.getSize(),
            clampedGrid.getPosition(),
            marginMm
        )._initWithGrid(clampedGrid);
    }

    /**
     * Instance helper to get a union covering this and other texture (aligned grids).
     */
    public unionAligned(other: ContourTexture, marginMm = 0): ContourTexture {
        return ContourTexture.createUnionAligned(this, other, marginMm);
    }

    /**
     * Create an empty ContourTexture covering the intersection
     * of two aligned textures. Returns undefined if they don't
     * overlap.
     */
    public static createIntersectionAligned(
        a: ContourTexture,
        b: ContourTexture
    ): ContourTexture | undefined {
        const intersectGrid = Grid.intersectAligned(
            a.textureGrid,
            b.textureGrid
        );
        if (intersectGrid.isEmpty) {
            return undefined;
        }
        const clampedGrid = ContourTexture.clampGridToImage(
            intersectGrid,
            a.grid
        );
        return new ContourTexture(
            a.grid,
            clampedGrid.getSize(),
            clampedGrid.getPosition(),
            a.margin
        )._initWithGrid(clampedGrid);
    }

    /**
     * Clamp a texture grid to the image grid bounds.
     * Returns the intersection grid, or a minimal 1x1x1 grid at the
     * image origin if the texture is entirely outside the image.
     */
    private static clampGridToImage(textureGrid: Grid, imageGrid: Grid): Grid {
        const clamped = Grid.intersectAligned(textureGrid, imageGrid);
        if (clamped.isEmpty) {
            // Contour entirely outside image — return minimal grid
            return new Grid(
                1,
                1,
                1,
                imageGrid.resX,
                imageGrid.resY,
                imageGrid.resZ,
                imageGrid.getTransformation()
            );
        }
        return clamped;
    }

    /**
     * Crop this texture's grid and pixel data to the image volume
     * bounds. If the texture already fits within the image, this is
     * a no-op.
     */
    public cropToImageBounds(): void {
        const clampedGrid = Grid.intersectAligned(
            this._textureGrid,
            this._imageGrid
        );

        // Already within bounds
        if (
            clampedGrid.sizeX === this._textureGrid.sizeX &&
            clampedGrid.sizeY === this._textureGrid.sizeY &&
            clampedGrid.sizeZ === this._textureGrid.sizeZ
        ) {
            return;
        }

        if (clampedGrid.isEmpty) {
            // Texture entirely outside image — reset to minimal
            const minGrid = new Grid(
                1,
                1,
                1,
                this._imageGrid.resX,
                this._imageGrid.resY,
                this._imageGrid.resZ,
                this._imageGrid.getTransformation()
            );
            this.resetSdfAndGrid([new Uint8Array(1)], minGrid);
            return;
        }

        // Voxel offset between old and new grid origins.
        // Both grids share orientation / resolution.
        const voxelOffset = this._textureGrid.toVoxelSpace(
            clampedGrid.getPosition()
        );
        const ox = Math.round(voxelOffset[0]);
        const oy = Math.round(voxelOffset[1]);
        const oz = Math.round(voxelOffset[2]);

        const oldSizeX = this._textureGrid.sizeX;
        const newSizeX = clampedGrid.sizeX;
        const newSizeY = clampedGrid.sizeY;
        const newSizeZ = clampedGrid.sizeZ;

        const srcPixels = this._sdf.toArray();
        const croppedSlices: Uint8Array[] = new Array(newSizeZ);

        for (let z = 0; z < newSizeZ; z++) {
            const srcZ = z + oz;
            const slice = new Uint8Array(newSizeX * newSizeY);
            if (srcZ >= 0 && srcZ < this._textureGrid.sizeZ) {
                const srcSlice = srcPixels[srcZ];
                for (let y = 0; y < newSizeY; y++) {
                    const srcY = y + oy;
                    if (srcY >= 0 && srcY < this._textureGrid.sizeY) {
                        const srcRowStart = srcY * oldSizeX + ox;
                        const dstRowStart = y * newSizeX;
                        for (let x = 0; x < newSizeX; x++) {
                            slice[dstRowStart + x] = srcSlice[srcRowStart + x];
                        }
                    }
                }
            }
            croppedSlices[z] = slice;
        }

        this.resetSdfAndGrid(croppedSlices, clampedGrid);
    }

    /**
     * Crop this texture's grid and pixel data to the actual SDF
     * content bounds (non-zero pixels), then clamp the result to
     * the image volume. This produces a tight bounding box around
     * drawn contours instead of retaining the original oversized
     * grid.
     */
    public cropToContentBounds(): void {
        if (this.isEmpty) {
            return;
        }

        // Delegate pixel cropping to SignedDistanceField.
        // Threshold 0: catch all non-zero pixels (full SDF
        // falloff region). 1 px margin for safety.
        const result = this._sdf.cropToContentBounds(0, 1);

        if (result.kind === 'empty') {
            // No content — reset to minimal 1×1×1 grid
            const minGrid = new Grid(
                1,
                1,
                1,
                this._imageGrid.resX,
                this._imageGrid.resY,
                this._imageGrid.resZ,
                this._imageGrid.getTransformation()
            );
            this.resetSdfAndGrid([new Uint8Array(1)], minGrid);
            return;
        }

        if (result.kind === 'tight') {
            // Bounds already cover the full grid — nothing to do.
            return;
        }

        const { sdf: croppedSdf, offset } = result;

        // Compute new grid origin from pixel offset
        const newOrigin = this._textureGrid.toWorldSpace(offset);
        const orientationQuat = quat.fromMat3(
            quat.create(),
            this._textureGrid.getOrientation()
        );
        const transform = mat4.fromRotationTranslation(
            mat4.create(),
            orientationQuat,
            newOrigin
        );
        const croppedGrid = new Grid(
            croppedSdf.size[0],
            croppedSdf.size[1],
            croppedSdf.size[2],
            this._textureGrid.resX,
            this._textureGrid.resY,
            this._textureGrid.resZ,
            transform
        );

        // Swap SDF directly (avoids redundant GPU read + upload)
        this._sdf.delete();
        this._sdf = croppedSdf;
        this._textureGrid = croppedGrid;
        this.invalidateCachedPixels();

        // Clamp to image bounds as a safety net
        this.cropToImageBounds();
    }

    /**
     * Expand the texture grid so it fully covers the image grid.
     * Existing SDF data is repositioned within the larger grid's
     * coordinate system so existing data keeps its exact position
     * with no rounding or resampling.
     */
    public expandToImageGrid(): void {
        const texGrid = this._textureGrid;
        const imgGrid = this._imageGrid;

        // Image corners in texture voxel space.
        const imgOrigin = texGrid.toVoxelSpace(imgGrid.getPosition());
        const imgEnd = texGrid.toVoxelSpace(
            vec3.add(vec3.create(), imgGrid.getPosition(), imgGrid.getSizeMM())
        );

        // How many texture voxels to add on each side
        // so the texture fully covers the image.
        const minX = Math.min(imgOrigin[0], imgEnd[0]);
        const minY = Math.min(imgOrigin[1], imgEnd[1]);
        const maxX = Math.max(imgOrigin[0], imgEnd[0]);
        const maxY = Math.max(imgOrigin[1], imgEnd[1]);

        const rawExpandBeforeX = -minX;
        const rawExpandBeforeY = -minY;
        const rawExpandAfterX = maxX - texGrid.sizeX;
        const rawExpandAfterY = maxY - texGrid.sizeY;

        // Tolerance to absorb floating-point drift from
        // mat3→quat→mat4 round-trips in expandOrShrink / Grid
        // constructor, which can shift voxel coordinates by ~1e-4.
        const eps = 0.01;
        if (
            rawExpandBeforeX < eps &&
            rawExpandBeforeY < eps &&
            rawExpandAfterX < eps &&
            rawExpandAfterY < eps
        ) {
            return;
        }

        const expandBeforeX = Math.max(0, Math.ceil(rawExpandBeforeX));
        const expandBeforeY = Math.max(0, Math.ceil(rawExpandBeforeY));
        const expandAfterX = Math.max(0, Math.ceil(rawExpandAfterX));
        const expandAfterY = Math.max(0, Math.ceil(rawExpandAfterY));

        const oldSizeX = texGrid.sizeX;
        const oldSizeY = texGrid.sizeY;
        const sizeZ = texGrid.sizeZ;

        const newSizeX = oldSizeX + expandBeforeX + expandAfterX;
        const newSizeY = oldSizeY + expandBeforeY + expandAfterY;

        const newGrid = texGrid
            .expandOrShrink(expandBeforeX, expandBeforeY, 0, true)
            .expandOrShrink(
                expandAfterX - expandBeforeX,
                expandAfterY - expandBeforeY,
                0,
                false
            );

        const oldData = this._sdf.toArray();
        const newData: Uint8Array[] = [];
        for (let z = 0; z < sizeZ; z++) {
            const newSlice = new Uint8Array(newSizeX * newSizeY);
            const oldSlice = oldData[z];
            for (let y = 0; y < oldSizeY; y++) {
                newSlice.set(
                    oldSlice.subarray(y * oldSizeX, (y + 1) * oldSizeX),
                    (y + expandBeforeY) * newSizeX + expandBeforeX
                );
            }
            newData.push(newSlice);
        }

        this.resetSdfAndGrid(newData, newGrid);

        // Warm the pixel cache so the first startModify()
        // call does not trigger a synchronous GPU readback.
        this._sdf.setCachedPixels(newData);

        // Snapshot the SDF version and grid after the new SDF
        // is fully set up. The volume hasn't changed (only
        // zero-padding was added), so mark the cache as current.
        this._volumeCacheVersion = this._sdf.dataVersion;
        this._volumeCacheGrid = this._textureGrid;
    }

    /**
     * Internal helper to finalize grid replacement when creating union.
     * (Keeps constructor usage minimal).
     */
    private _initWithGrid(grid: Grid): ContourTexture {
        this._textureGrid = grid;
        this._sdf.resize(grid.getSize());
        // Clear texture to 0 (outside) to avoid undefined values in boolean operations
        this._sdf.clearTexture();
        this.invalidateCachedPixels();
        return this;
    }
}
